import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { PlanfixImportJournalService } from './planfix-import-journal.service';
import { PlanfixImportExportService } from './planfix-import-export.service';
import {
  PLANFIX_IMPORT_ACTION, PLANFIX_IMPORT_PHASE_KEYS, PLANFIX_IMPORT_STATUS_KEYS,
  PlanfixImportAction, PlanfixImportPage, PlanfixImportRow, PlanfixImportRun,
} from './planfix-import-journal.model';

const FIRST_PAGE = 0;
const INITIAL_ACTION_VERSION = 0;
const NO_ERROR = '';
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const UNKNOWN_KEY = 'PLANFIX_IMPORT_UNKNOWN';
const LIST_ERROR_KEY = 'PLANFIX_IMPORT_LIST_ERROR';
const LOG_ERROR_KEY = 'PLANFIX_IMPORT_LOG_ERROR';
const DENIED_KEY = 'PLANFIX_IMPORT_DENIED';
const EXPIRED_KEY = 'PLANFIX_IMPORT_EXPIRED';
const DOWNLOAD_ERROR_KEY = 'PLANFIX_IMPORT_DOWNLOAD_ERROR';

@Component({
  selector: 'app-planfix-import-journal',
  templateUrl: './planfix-import-journal.component.html',
  styleUrls: ['./planfix-import-journal.component.css'],
  providers: [PlanfixImportJournalService, PlanfixImportExportService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanfixImportJournalComponent implements OnInit, OnDestroy {
  readonly actions = PLANFIX_IMPORT_ACTION;
  rows: PlanfixImportRow[] = [];
  page = FIRST_PAGE;
  hasMore = false;
  loading = false;
  listErrorKey = NO_ERROR;
  selectedRun: PlanfixImportRow | null = null;
  logText: string | null = null;
  logLoading = false;
  logErrorKey = NO_ERROR;
  copyPending = false;
  copied = false;
  copyFailed = false;
  private pageRequest: Subscription;
  private logRequest: Subscription;
  private actionVersion = INITIAL_ACTION_VERSION;
  private destroyed = false;

  constructor(private journal: PlanfixImportJournalService,
              private exporter: PlanfixImportExportService,
              private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadPage(FIRST_PAGE);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.cancelLog();
    if (this.pageRequest) {
      this.pageRequest.unsubscribe();
    }
  }

  loadPage(page: number): void {
    if (page < FIRST_PAGE) {
      return;
    }
    if (this.pageRequest) {
      this.pageRequest.unsubscribe();
    }
    this.closeLog();
    this.page = page;
    this.loading = true;
    this.listErrorKey = NO_ERROR;
    this.rows = [];
    this.hasMore = false;
    this.pageRequest = this.journal.fetchPage(page).subscribe(
      result => this.showPage(result),
      error => this.showListError(error),
    );
  }

  openLog(run: PlanfixImportRow, action: PlanfixImportAction): void {
    this.closeLog();
    this.selectedRun = run;
    this.logLoading = true;
    const version = this.actionVersion;
    this.logRequest = this.journal.fetchLog(run.id).subscribe(
      text => this.showLog(text, action, version),
      error => this.showLogError(error, version),
    );
  }

  closeLog(): void {
    this.cancelLog();
    this.selectedRun = null;
    this.logText = null;
    this.logErrorKey = NO_ERROR;
    this.copied = false;
    this.copyFailed = false;
  }

  trackByRunId(index: number, run: PlanfixImportRow): string {
    return run.id;
  }

  private cancelLog(): void {
    this.actionVersion++;
    if (this.logRequest) {
      this.logRequest.unsubscribe();
    }
    this.logLoading = false;
    this.copyPending = false;
  }

  private showPage(result: PlanfixImportPage): void {
    this.rows = result.items.map(run => this.toRow(run));
    this.hasMore = result.hasMore;
    this.loading = false;
    this.cdr.markForCheck();
  }

  private toRow(run: PlanfixImportRun): PlanfixImportRow {
    return Object.assign({}, run, {
      statusKey: PLANFIX_IMPORT_STATUS_KEYS[run.status] || UNKNOWN_KEY,
      phaseKey: PLANFIX_IMPORT_PHASE_KEYS[run.phase] || UNKNOWN_KEY,
    });
  }

  private showListError(error: { status: number }): void {
    this.loading = false;
    this.listErrorKey = error.status === HTTP_FORBIDDEN ? DENIED_KEY : LIST_ERROR_KEY;
    this.cdr.markForCheck();
  }

  private showLog(text: string, action: PlanfixImportAction, version: number): void {
    if (!this.isCurrent(version)) {
      return;
    }
    this.logText = text;
    this.logLoading = false;
    if (action === PLANFIX_IMPORT_ACTION.COPY) {
      this.copyLog(text, version);
    }
    if (action === PLANFIX_IMPORT_ACTION.DOWNLOAD) {
      this.downloadLog(text);
    }
    this.cdr.markForCheck();
  }

  private showLogError(error: { status: number }, version: number): void {
    if (!this.isCurrent(version)) {
      return;
    }
    this.logLoading = false;
    this.logErrorKey = error.status === HTTP_NOT_FOUND ? EXPIRED_KEY : LOG_ERROR_KEY;
    if (error.status === HTTP_FORBIDDEN) {
      this.logErrorKey = DENIED_KEY;
    }
    this.cdr.markForCheck();
  }

  private copyLog(text: string, version: number): void {
    this.copyPending = true;
    this.exporter.copy(text, () => this.isCurrent(version)).then(
      copied => this.showCopyResult(copied, version),
      () => this.showCopyResult(false, version),
    );
  }

  private showCopyResult(copied: boolean, version: number): void {
    if (!this.isCurrent(version)) {
      return;
    }
    this.copyPending = false;
    this.copied = copied;
    this.copyFailed = !copied;
    this.cdr.markForCheck();
  }

  private downloadLog(text: string): void {
    try {
      this.exporter.download(this.selectedRun.id, text);
    } catch (error) {
      this.logErrorKey = DOWNLOAD_ERROR_KEY;
    }
  }

  private isCurrent(version: number): boolean {
    return !this.destroyed && version === this.actionVersion;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/planfix-import-journal/planfix-import-journal.component.ts